// Rooms and scene builds on the client (contracts sections 2 and 9).
//  - rooms.json: room -> scene (+ channel or pinned build, portal links). Static file, so it works without the server.
//  - scenes/registry.json: scene -> channels -> build -> folder. The client follows the room's channel (default
//    "live"); `?channel=` and `?build=` override it for the first room (testing a preview build).
//  - Without a registry (or a scene missing from it) the scene folder itself is tried (the layout before builds).

import { FALLBACK_ROOMS, hostSite, parseRoomsFile, roomInfo, siteDefaultRoom, type RoomInfo, type RoomsFile } from '@lr3d/shared';
import { loadManifestAt, SCENES, type LoadedManifest } from './manifest';

/** Builds may require a newer client (registry `min_client`). Bump when the client stops reading older manifests. */
export const CLIENT_VERSION = 1;

const BASE = import.meta.env.BASE_URL;

interface RegistryBuild { dir: string; min_client?: number; variant?: string }
interface RegistryScene { channels: Record<string, string>; builds: Record<string, RegistryBuild> }
interface Registry { version: number; scenes: Record<string, RegistryScene> }

let roomsP: Promise<RoomsFile> | null = null;
/** Replace the rooms file for the rest of the page (a room connection set applied to it, room/experience.ts). */
export function useRooms(f: RoomsFile): void {
  roomsP = Promise.resolve(f);
}

/** rooms.json (once per page; a bad or missing file falls back to "every room uses the listening room"). */
export function loadRooms(): Promise<RoomsFile> {
  return (roomsP ??= fetch(`${BASE}rooms.json`, { cache: 'no-cache' })
    .then((r) => (r.ok && (r.headers.get('content-type') || '').includes('json') ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((j) => parseRoomsFile(j))
    .catch((e) => {
      console.warn('[rooms] rooms.json unusable; every room uses the listening room scene', e);
      return FALLBACK_ROOMS;
    }));
}

/** The registry, fetched again for each room load (a promotion reaches the next room load). Null if missing. */
async function loadRegistry(): Promise<Registry | null> {
  try {
    const r = await fetch(`${SCENES}registry.json`, { cache: 'no-cache' });
    if (!r.ok || !(r.headers.get('content-type') || '').includes('json')) return null;
    const j = (await r.json()) as Registry;
    return j.version === 1 ? j : null;
  } catch {
    return null;
  }
}

export interface SceneChoice {
  /** Scene id override (`?scene=`). */
  scene?: string | null;
  /** Channel override (`?channel=`). */
  channel?: string | null;
  /** Build id override (`?build=`). */
  build?: string | null;
}

export interface RoomScene extends LoadedManifest {
  info: RoomInfo;
  scene: string;
  /** Build id from the registry, null for a scene folder without a registry entry. */
  buildId: string | null;
}

/** The room's own start point (rooms.json `spawn`) in place of the manifest's: where a first visit starts. */
function withSpawn(m: LoadedManifest, info: RoomInfo): LoadedManifest {
  return info.spawn ? { ...m, manifest: { ...m.manifest, spawn: { position: [...info.spawn.position], yaw_deg: info.spawn.yaw_deg } } } : m;
}

/** Resolve a room to its scene build and load the build's manifest. */
export async function loadRoomScene(room: string, over: SceneChoice = {}): Promise<RoomScene> {
  const rooms = await loadRooms();
  const info = roomInfo(rooms, room);
  if (!info) throw new Error(`bad room id ${room}`);
  const scene = over.scene || info.scene;
  const reg = await loadRegistry();
  const s = reg?.scenes[scene];
  if (s) {
    const buildId = over.build || (over.channel ? s.channels[over.channel] : null) || info.build || s.channels[info.channel] || s.channels.live;
    const b = buildId ? s.builds[buildId] : undefined;
    if (!buildId || !b) throw new Error(`scene ${scene}: no build ${buildId ?? `on channel ${over.channel ?? info.channel}`}`);
    if ((b.min_client ?? 1) > CLIENT_VERSION) throw new Error(`scene ${scene} build ${buildId} needs a newer client: reload the page`);
    const dir = b.dir === '.' ? '' : `${b.dir.replace(/\/+$/, '')}/`;
    const m = await loadManifestAt(`${SCENES}${scene}/${dir}`);
    if (!m) throw new Error(`scene ${scene} build ${buildId}: manifest unusable`);
    return { ...withSpawn(m, info), info, scene, buildId };
  }
  // no registry entry: the scene folder itself (layout before the registry), then the placeholder for the default room
  for (const id of over.scene || info.known ? [scene] : [scene, 'placeholder']) {
    const m = await loadManifestAt(`${SCENES}${id}/`);
    if (m) return { ...withSpawn(m, info), info, scene: id, buildId: null };
  }
  throw new Error(`scene ${scene} of room ${room} is not built yet (not in scenes/registry.json and no scenes/${scene}/manifest.json)`);
}

/** The room named by the last path segment (`/gallery_room`), if it is a listed room. One segment, no trailing
 *  slash: the page's relative base (vite `base: './'`) then still resolves to the app's folder. */
function pathRoom(rooms: RoomsFile, url: URL): string | null {
  const seg = decodeURIComponent(url.pathname.slice(url.pathname.lastIndexOf('/') + 1));
  return seg && seg !== 'index.html' && rooms.rooms[seg] ? seg : null;
}

/** The room at `/` on this page: the default room of the host's site (docs/sites-and-domains.md; `?site=<id>` on a
 *  host that is not a site, e.g. localhost), else rooms.json's default room. */
export function homeRoom(rooms: RoomsFile, url: URL = new URL(location.href)): string {
  const q = url.searchParams.get('site');
  return siteDefaultRoom(rooms, hostSite(rooms, url.hostname) ?? (q && rooms.sites?.[q] ? q : null));
}

/** The room the page URL names (`?room=`, else the path `/<room>`), or null. */
export function namedRoom(rooms: RoomsFile, url: URL = new URL(location.href)): string | null {
  const r = url.searchParams.get('room');
  if (r && roomInfo(rooms, r)) return r;
  return pathRoom(rooms, url);
}

/** Room id from the page URL (`?room=`, else the path `/<room>`), else the default room (homeRoom). */
export function roomFromUrl(rooms: RoomsFile, url: URL = new URL(location.href)): string {
  return namedRoom(rooms, url) ?? homeRoom(rooms, url);
}

/**
 * The URL for `room`, keeping the other parameters (server, tier, flags, a room set's `x`) and dropping the first
 * room's overrides. With a room set (`?x=`) the default room is named too (`/listening_room?x=...`), because `/?x=`
 * opens the set's start room.
 */
export function urlForRoom(room: string, defaultRoom: string, url: URL = new URL(location.href), rooms?: RoomsFile): string {
  const u = new URL(url.href);
  for (const k of ['scene', 'channel', 'build']) u.searchParams.delete(k);
  const atDir = room === defaultRoom && !u.searchParams.has('x');
  if (rooms && !u.searchParams.has('room')) {
    // path style (`/gallery_room`, the default room at the folder itself), unless the page uses `?room=`
    const dir = u.pathname.slice(0, u.pathname.lastIndexOf('/') + 1);
    u.pathname = atDir ? dir : dir + encodeURIComponent(room);
    return u.pathname + u.search + u.hash;
  }
  if (atDir) u.searchParams.delete('room');
  else u.searchParams.set('room', room);
  return u.pathname + u.search + u.hash;
}
